"use client";

import React, { useState } from "react";
import { Phone, MessageCircle, Mail, Copy, Check } from "lucide-react";

import Header from "../../components/common/Header";
import Footer from "../../components/common/Footer";
import UseHotjar from "../../components/common/HotJar/HotJar";

import {
  COMPANY_DISCORD_INVITE_LINK,
  COMPANY_MEDIA_EMAIL,
  COMPANY_PARTNERSHIP_EMAIL,
  COMPANY_SUPPORT_EMAIL,
} from "@/constants/sleapContact";

import { useTranslations } from "next-intl";
import { useToast } from "@/app/hooks/toast/useToast";

type FaqItem = {
  question: string;
  answer: string;
};

type FaqCategory = {
  category: string;
  categoryKey: string;
  questions: FaqItem[];
};

export default function ContactClient() {
  const t = useTranslations("ContactPage");
  const { showToast } = useToast();
  const [copied, setCopied] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });
  const [formErrors, setFormErrors] = useState({
    firstName: "",
    lastName: "",
    email: "",
    message: "",
  });

  // Get FAQ categories and questions from translations
  const faqCategories = [
    { key: "cryptoPayments", label: t("faq.categories.cryptoPayments") },
    { key: "hotelFlight", label: t("faq.categories.hotelFlight") },
    { key: "cancellations", label: t("faq.categories.cancellations") },
    { key: "partialPayments", label: t("faq.categories.partialPayments") },
    { key: "nft", label: t("faq.categories.nft") },
    { key: "credits", label: t("faq.categories.credits") },
    { key: "vouchers", label: t("faq.categories.vouchers") },
  ];

  const [activeCategory, setActiveCategory] = useState<string>(
    faqCategories[0].key
  );

  const getFaqQuestions = (categoryKey: string): FaqItem[] => {
    try {
      const questions = t.raw(`faq.questions.${categoryKey}`) as FaqItem[];
      return Array.isArray(questions) ? questions : [];
    } catch {
      return [];
    }
  };

  const faqData: FaqCategory[] = faqCategories.map((category) => ({
    category: category.label,
    categoryKey: category.key,
    questions: getFaqQuestions(category.key),
  }));

  const currentCategoryData =
    faqData.find((c) => c.categoryKey === activeCategory) || faqData[0];

  const copyToClipboard = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1800);
    } catch (err) {
      console.error("Copy failed", err);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateForm = (): boolean => {
    const errors = {
      firstName: "",
      lastName: "",
      email: "",
      message: "",
    };

    if (!formData.firstName.trim()) {
      errors.firstName = t("form.validation.firstNameRequired");
    }

    if (!formData.lastName.trim()) {
      errors.lastName = t("form.validation.lastNameRequired");
    }

    if (!formData.email.trim()) {
      errors.email = t("form.validation.emailRequired");
    } else if (!validateEmail(formData.email)) {
      errors.email = t("form.validation.emailInvalid");
    }

    if (!formData.message.trim()) {
      errors.message = t("form.validation.messageRequired");
    } else if (formData.message.trim().length < 10) {
      errors.message = t("form.validation.messageMinLength");
    }

    setFormErrors(errors);
    return !Object.values(errors).some((error) => error !== "");
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (formErrors[name as keyof typeof formErrors]) {
      setFormErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        showToast(t("form.success"), "success");
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          message: "",
        });
        setFormErrors({
          firstName: "",
          lastName: "",
          email: "",
          message: "",
        });
      } else {
        showToast(t("form.error"), "error");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      showToast(t("form.error"), "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const ContactItem = ({
    icon: Icon,
    value,
    href,
    isCopy = false,
    copyText,
  }: {
    icon: React.ComponentType<{ className?: string }>;
    value: string;
    href?: string;
    isCopy?: boolean;
    copyText?: string;
  }) => {
    return (
      <div className="flex items-center gap-1.5">
        <Icon className="w-4 h-4 text-gray-600 dark:text-gray-400 shrink-0" />

        <div className="flex items-center gap-1">
          {href ? (
            <a
              href={href}
              className="text-sm text-gray-900 dark:text-white underline hover:text-cyan-400 dark:hover:text-cyan-300 transition-colors"
              target={href.startsWith("http") ? "_blank" : undefined}
              rel="noopener noreferrer"
            >
              {value}
            </a>
          ) : (
            <span className="text-sm text-gray-900 dark:text-white">
              {value}
            </span>
          )}

          {isCopy && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                copyToClipboard(copyText || value, value);
              }}
              className="p-0.5 text-gray-500 hover:text-gray-300 dark:hover:text-gray-300 transition-colors"
              title="Copy to clipboard"
            >
              {copied === value ? (
                <Check className="w-4 h-4 text-green-500" />
              ) : (
                <Copy className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              )}
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="relative min-h-screen flex flex-col bg-zinc-50 dark:bg-zinc-950 overflow-hidden font-sans">
      <Header />

      <main className="flex-grow relative">
        <div className="absolute inset-0 pointer-events-none opacity-30">
          <div className="hidden md:block">
            <div className="absolute w-[1055px] h-72 left-[25%] top-1/2 -translate-y-1/2 rotate-[-7.8deg] bg-cyan-400 rounded-full blur-3xl" />
            <div className="absolute w-[865px] h-96 left-0 top-[60%] rotate-[-13.3deg] bg-violet-400 rounded-full blur-3xl" />
          </div>
          <div className="md:hidden">
            <div className="absolute w-[400px] h-48 -left-20 top-[30%] rotate-[-29deg] bg-cyan-400 rounded-full blur-3xl" />
            <div className="absolute w-[300px] h-64 -right-32 bottom-[20%] rotate-[15deg] bg-violet-400 rounded-full blur-3xl" />
          </div>
        </div>

        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 pt-16 md:pt-20 pb-8 md:pb-12 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-semibold font-inter text-gray-900 dark:text-white leading-tight md:leading-10">
            {t("heading")}
          </h1>
          <p className="mt-4 text-base sm:text-lg text-gray-600 dark:text-gray-400 leading-6 max-w-xl mx-auto">
            {t("intro")}
          </p>
        </div>

        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pb-16 md:pb-24">
          <div className="flex flex-col md:flex-row justify-center items-start gap-10 md:gap-14">
            <div className="w-full max-w-md md:w-64 mx-auto md:mx-0 flex flex-col gap-7 md:gap-8 text-center md:text-left py-6 md:py-8 px-6 md:px-0">
              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center mb-2">
                  <div className="h-5" />
                  {t("cards.general.title")}
                </h3>
                <ContactItem
                  icon={MessageCircle}
                  value={t("discord")}
                  href={COMPANY_DISCORD_INVITE_LINK}
                />
                <ContactItem
                  icon={Mail}
                  value={COMPANY_SUPPORT_EMAIL}
                  href={`mailto:${COMPANY_SUPPORT_EMAIL}`}
                  isCopy
                />
              </div>

              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center mb-2">
                  <div className="h-5" /> {t("cards.partnerships.title")}
                </h3>
                <ContactItem
                  icon={Mail}
                  value={COMPANY_PARTNERSHIP_EMAIL}
                  href={`mailto:${COMPANY_PARTNERSHIP_EMAIL}`}
                  isCopy
                />
              </div>

              <div>
                <h3 className="text-base font-semibold text-gray-900 dark:text-white flex items-center  mb-2">
                  <div className="h-5" /> {t("cards.media.title")}
                </h3>
                <ContactItem
                  icon={Mail}
                  value={COMPANY_MEDIA_EMAIL}
                  href={`mailto:${COMPANY_MEDIA_EMAIL}`}
                  isCopy
                />
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              className="w-full max-w-lg md:w-[512px] mx-auto md:mx-0 rounded-lg bg-white dark:bg-zinc-900 outline outline-1 outline-gray-200 dark:outline-zinc-700 outline-offset-[-1px] px-5 sm:px-6 py-6 flex flex-col gap-6"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div className="space-y-2.5">
                  <label className="text-sm font-medium text-gray-900 dark:text-white">
                    {t("form.firstName")}
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder={t("form.firstNamePlaceholder")}
                    className={`w-full px-3 py-2.5 bg-gray-50 dark:bg-zinc-800/70 rounded-xl border ${
                      formErrors.firstName
                        ? "border-red-500"
                        : "border-gray-300 dark:border-zinc-700"
                    } shadow-sm text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400`}
                  />
                  {formErrors.firstName && (
                    <p className="text-xs text-red-500">
                      {formErrors.firstName}
                    </p>
                  )}
                </div>

                <div className="space-y-2.5">
                  <label className="text-sm font-medium text-gray-900 dark:text-white">
                    {t("form.lastName")}
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder={t("form.lastNamePlaceholder")}
                    className={`w-full px-3 py-2.5 bg-gray-50 dark:bg-zinc-800/70 rounded-xl border ${
                      formErrors.lastName
                        ? "border-red-500"
                        : "border-gray-300 dark:border-zinc-700"
                    } shadow-sm text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400`}
                  />
                  {formErrors.lastName && (
                    <p className="text-xs text-red-500">
                      {formErrors.lastName}
                    </p>
                  )}
                </div>
              </div>

              <div className="space-y-2.5">
                <label className="text-sm font-medium text-gray-900 dark:text-white">
                  {t("form.email")}
                </label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t("form.emailPlaceholder")}
                  className={`w-full px-3 py-2.5 bg-gray-50 dark:bg-zinc-800/70 rounded-xl border ${
                    formErrors.email
                      ? "border-red-500"
                      : "border-gray-300 dark:border-zinc-700"
                  } shadow-sm text-sm text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400`}
                />
                {formErrors.email && (
                  <p className="text-xs text-red-500">{formErrors.email}</p>
                )}
              </div>

              <div className="space-y-4">
                <div className="space-y-2.5">
                  <textarea
                    rows={6}
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    placeholder={t("form.messagePlaceholder")}
                    className={`w-full p-3.5 bg-gray-50 dark:bg-zinc-800/70 rounded-xl border ${
                      formErrors.message
                        ? "border-red-500"
                        : "border-gray-300 dark:border-zinc-700"
                    } shadow-sm text-sm text-gray-900 dark:text-white resize-none placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-cyan-400`}
                  />
                  {formErrors.message && (
                    <p className="text-xs text-red-500">{formErrors.message}</p>
                  )}
                </div>
                <div className="flex">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="
                      px-5 py-2.5
                      bg-gradient-to-br from-cyan-300 to-violet-500
                      rounded-xl text-white text-sm font-inter
                      shadow-md hover:from-cyan-400 hover:to-violet-600 transition
                      disabled:opacity-50 disabled:cursor-not-allowed
                    "
                  >
                    {isSubmitting ? "Sending..." : t("form.sendButton")}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
        <div className="relative max-w-3xl mx-auto px-5 sm:px-8 pb-20 md:pb-32">
          <h2 className="text-3xl sm:text-4xl font-semibold font-inter text-center text-gray-900 dark:text-white mb-8 md:mb-10">
            {t("faq.heading")}
          </h2>

          <div className="flex flex-wrap justify-center gap-3 mb-10 md:mb-12">
            {faqData.map((item) => (
              <button
                key={item.categoryKey}
                onClick={() => setActiveCategory(item.categoryKey)}
                className={`
                  px-5 py-2.5 rounded-xl border text-base font-medium font-inter transition-all duration-200
                  ${
                    activeCategory === item.categoryKey
                      ? "bg-cyan-900/60 border-cyan-500 text-cyan-200 shadow-lg scale-105"
                      : "bg-white dark:bg-zinc-800/50 border-gray-200 dark:border-zinc-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-zinc-700 hover:scale-105"
                  }
                `}
              >
                {item.category}
              </button>
            ))}
          </div>

          <div className="divide-y divide-gray-200 dark:divide-zinc-800">
            {currentCategoryData.questions.length > 0 ? (
              currentCategoryData.questions.map((item, index) => (
                <details key={index} className="group py-4 md:py-5">
                  <summary className="flex justify-between items-center cursor-pointer list-none hover:bg-gray-50 dark:hover:bg-zinc-900/50 rounded-lg px-2 py-1 transition">
                    <span className="text-base font-medium font-inter text-gray-900 dark:text-white pr-4">
                      {item.question}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400 group-open:rotate-180 transition-transform text-xl shrink-0">
                      ↓
                    </span>
                  </summary>
                  <p className="mt-3 md:mt-4 text-base text-gray-600 dark:text-gray-400 leading-6 px-2 whitespace-pre-line">
                    {item.answer}
                  </p>
                </details>
              ))
            ) : (
              <p className="text-center text-gray-500 dark:text-gray-400 py-12">
                {t("faq.noQuestions")}
              </p>
            )}
          </div>
        </div>
      </main>

      <Footer />
      <UseHotjar />
    </div>
  );
}
